import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBackRounded,
  SearchRounded,
  PrecisionManufacturingRounded,
} from "@mui/icons-material";
import useTranslation from "../../hooks/useTranslation";
import { useAppSelector, useAppDispatch } from "../../hooks/sharedHooks";
import { Machine } from "../../../customer/machinery/models/models";
import { getMachines } from "../../../customer/machinery/customerMachinerySlice";
import { dt } from "../../themes/redesignTokens";

// ================================================================
//  MIDAS Design Tokens (local subset)
// ================================================================


const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldSoft: "rgba(212, 160, 23, 0.08)",
  goldBorder: "rgba(212, 160, 23, 0.4)",
  charcoal: "#1E1E2A",
};

// ================================================================
//  ChatMachinePicker (inline version — no Dialog)
// ================================================================

interface ChatMachinePickerProps {
  onClose: () => void;
  onSelect: (machine: Machine) => void;
  loading?: boolean;
}

const ChatMachinePicker: React.FC<ChatMachinePickerProps> = ({
  onClose,
  onSelect,
  loading = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");

  // Get machines from Redux store — machines are Record<string, Machine[]>
  const machinesMap = useAppSelector(
    (state) => state.customerMachinery.machines
  );
  const machinesLoading = useAppSelector(
    (state) => state.customerMachinery.loading
  );

  // Fetch machines when picker mounts if not already loaded
  useEffect(() => {
    if (!machinesMap || Object.keys(machinesMap).length === 0) {
      dispatch(getMachines({}));
    }
  }, [machinesMap, dispatch]);

  // Flatten all machine groups — show all machines (not just botActivated)
  const allMachines = useMemo(() => {
    const all: Machine[] = [];
    if (machinesMap) {
      Object.values(machinesMap).forEach((group) => {
        if (Array.isArray(group)) {
          all.push(...group);
        }
      });
    }
    return all;
  }, [machinesMap]);

  // Filter by search term
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allMachines;
    return allMachines.filter(
      (m) =>
        m.model.toLowerCase().includes(term) ||
        m.serialNumber.toLowerCase().includes(term) ||
        (m.manufacturer?.name || "").toLowerCase().includes(term) ||
        m.internalNumber.toLowerCase().includes(term)
    );
  }, [allMachines, search]);

  const handleSelect = (machine: Machine) => {
    setSearch("");
    onSelect(machine);
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        background: dt.pageBg,
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(52,152,219,0.04) 1px, transparent 0)",
        backgroundSize: "28px 28px",
        overflow: "hidden",
      }}
    >
      {/* Header bar with back button + title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 12px 0 12px",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: dt.textSecondary,
            "&:hover": { color: dt.textPrimary, backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          <ArrowBackRounded sx={{ fontSize: "20px" }} />
        </IconButton>
        <PrecisionManufacturingRounded
          sx={{ fontSize: "18px", color: midas.gold }}
        />
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "15px",
            fontWeight: 700,
            color: dt.textPrimary,
          }}
        >
          {translate("machinePicker.title")}
        </Box>
      </Box>

      {/* Search field */}
      <Box sx={{ padding: "12px 16px", flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={translate("machinePicker.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: "18px", color: dt.textMuted }} />
              </InputAdornment>
            ),
            sx: {
              fontFamily: dt.font,
              fontSize: "14px",
              backgroundColor: dt.cardBg,
              borderRadius: "10px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: dt.cardBorder,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: midas.goldBorder,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: midas.gold,
                borderWidth: "1.5px",
              },
            },
          }}
        />
      </Box>

      {/* Loading spinner */}
      {(loading || machinesLoading) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px",
          }}
        >
          <CircularProgress size={28} sx={{ color: midas.gold }} />
        </Box>
      )}

      {/* Machine list */}
      {!loading && !machinesLoading && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "4px 0 8px 0",
          }}
        >
          {filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                padding: "32px 16px",
                fontFamily: dt.font,
                fontSize: "13px",
                color: dt.textMuted,
              }}
            >
              {allMachines.length === 0
                ? translate("machinePicker.noMachines")
                : translate("machinePicker.noResults")}
            </Box>
          ) : (
            filtered.map((machine) => (
              <Box
                key={machine.id}
                onClick={() => handleSelect(machine)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px 16px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: midas.goldSoft,
                  },
                }}
              >
                {/* Machine icon */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: midas.goldSoft,
                    border: `1px solid ${midas.goldBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PrecisionManufacturingRounded
                    sx={{ fontSize: "18px", color: midas.gold }}
                  />
                </Box>

                {/* Machine details */}
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.textPrimary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {machine.model}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      fontWeight: 500,
                      color: dt.textSecondary,
                      mt: "1px",
                    }}
                  >
                    {machine.serialNumber}
                    {machine.manufacturer?.name
                      ? ` \u00B7 ${machine.manufacturer.name}`
                      : ""}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChatMachinePicker;
